// src/urlstate.ts — compact, DEFENSIVE URL encoding of scenario state, shared by
// both modules (plan Step 8.3). Never trust the URL: unknown or out-of-range
// params are ignored/clamped, never applied raw. Pure; tested in isolation.

import type { Drivers } from "./model/entities.ts";
import type { EconInputs } from "./econ/types.ts";

/** Read a number from params, clamped to [min,max]; fall back if absent/invalid. */
export function num(params: URLSearchParams, key: string, min: number, max: number, fallback: number): number {
  const raw = params.get(key);
  if (raw == null) return fallback;
  const v = Number(raw);
  if (!Number.isFinite(v)) return fallback;
  return v < min ? min : v > max ? max : v;
}

// ---- econ inputs ----
const ECON_KEYS: Array<[keyof EconInputs, string, number, number]> = [
  ["capabilities", "ca", 0, 1],
  ["adoption", "ad", 0, 1],
  ["autonomy", "au", 0, 1],
  ["productivity", "pr", 1, 20],
  ["adjustment_months", "aj", 0, 120],
  ["new_task_share", "nt", 0, 1],
  ["horizon_year", "hz", 2026, 2060],
];

/** Decode econ inputs from the URL, clamped, falling back to `defaults`. */
export function econFromParams(params: URLSearchParams, defaults: EconInputs): EconInputs {
  const out = { ...defaults };
  for (const [field, key, min, max] of ECON_KEYS) out[field] = num(params, key, min, max, defaults[field]);
  return out;
}

/** Encode econ inputs as URL query pairs (compact). */
export function econToParams(inputs: EconInputs): Array<[string, string]> {
  return ECON_KEYS.map(([field, key]) => [key, String(round4(inputs[field]))]);
}

// ---- capex drivers (for the bridge + the return link) ----
const CAPEX_KEYS: Array<[keyof Drivers, string, number, number]> = [
  ["capex", "cx", 0.2, 2.0],
  ["ai", "ai", 0.2, 2.0],
  ["oa", "oa", 0, 1],
  ["an", "an", 0, 1],
  ["y10", "y10", 1.5, 9],
  ["y20", "y20", 1.5, 9],
  ["y30", "y30", 1.5, 9],
  ["sm", "sm", 0.4, 4],
  ["cost", "ct", 0.7, 1.7],
  ["pw", "pw", 0.6, 2.2],
  ["gas", "gs", 0.05, 0.7],
  ["life", "lf", 3, 7],
];

/** Decode capex drivers from the URL, clamped, falling back to `base`. */
export function capexFromParams(params: URLSearchParams, base: Drivers): Drivers {
  const out = { ...base };
  for (const [field, key, min, max] of CAPEX_KEYS) out[field] = num(params, key, min, max, base[field]);
  return out;
}

/** Encode capex drivers as URL query pairs. */
export function capexToParams(drivers: Drivers): Array<[string, string]> {
  return CAPEX_KEYS.map(([field, key]) => [key, String(round4(drivers[field]))]);
}

const round4 = (x: number): number => Math.round(x * 1e4) / 1e4;
